import { Activity, CheckCircle2, Clock, TriangleAlert } from 'lucide-react';
import { formatUptimePercent, summarizeMonitors } from '@/modules/monitoring/summary';
import type { Monitor } from '@/types/api';

/**
 * The four numbers that answer "is anything wrong?" before you read a single row.
 *
 * ## Why a summary at all
 *
 * The page opened straight onto a list. With three monitors that is fine; with thirty, finding the
 * one that is down means reading thirty rows, and the whole point of monitoring is not having to
 * look. This states the answer, and the list below explains it.
 *
 * ## Stale is its own count, not folded into "up"
 *
 * A monitor whose last check is hours old is not up — it is unknown, and the platform saying "up"
 * about a reading it has not refreshed is the single failure that makes monitoring worse than
 * having none. It gets its own tile so the number cannot quietly hide inside a healthy one.
 *
 * ## Shown before the first monitor too
 *
 * It used to appear only once a monitor existed, so an account that had not added one never saw
 * what the page watches for — "Not checked recently" and the uptime figure were simply absent, which
 * read as missing features rather than as nothing to count yet. The tiles are there from the start,
 * at zero, and the uptime tile says why it has no figure.
 */
export function MonitoringSummary({ monitors }: { monitors: Monitor[] }) {
  const summary = summarizeMonitors(monitors);

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
      <Tile icon={CheckCircle2} label="Up" value={String(summary.up)} tone={summary.up > 0 ? 'success' : 'muted'} />
      <Tile icon={TriangleAlert} label="Down" value={String(summary.down)} tone={summary.down > 0 ? 'danger' : 'muted'} />
      <Tile
        icon={Clock}
        label="Not checked recently"
        value={String(summary.stale)}
        tone={summary.stale > 0 ? 'warning' : 'muted'}
        hint={summary.stale > 0 ? 'Their status may be out of date' : undefined}
      />
      <Tile
        icon={Activity}
        label="Average uptime, 24h"
        value={summary.averageUptime === null ? 'No data yet' : formatUptimePercent(summary.averageUptime)}
        tone="muted"
        hint={
          summary.averageUptime !== null
            ? undefined
            : monitors.length === 0
              ? 'Add a monitor to start measuring'
              : 'Not enough checks yet'
        }
      />
    </div>
  );
}

function Tile({
  icon: Icon,
  label,
  value,
  tone,
  hint,
}: {
  icon: typeof Activity;
  label: string;
  value: string;
  tone: 'success' | 'danger' | 'warning' | 'muted';
  hint?: string | undefined;
}) {
  const toneClass = {
    success: 'text-success',
    danger: 'text-danger',
    warning: 'text-warning',
    muted: 'text-muted-foreground',
  }[tone];

  return (
    <div className="bg-card rounded-lg border p-4">
      <div className="text-muted-foreground flex items-center gap-2 text-xs font-medium tracking-wide uppercase">
        <Icon className={`size-3.5 ${toneClass}`} />
        {label}
      </div>
      <div className={`mt-2 text-2xl font-semibold tracking-tight tabular-nums ${toneClass}`}>{value}</div>
      {hint !== undefined && <div className="text-muted-foreground mt-1 text-xs">{hint}</div>}
    </div>
  );
}
